/**
 * CandidateWelcomePhoto — V1.8.6
 *
 * Optional post-signup step. Users land here right after
 * `/auth/candidate/register`; they can add a photo or skip.
 * Either way, they're forwarded to `?next=<dest>`.
 *
 * We deliberately keep this page dumb — it doesn't itself gate anything
 * or hit the server unless the user actually picks a file (that's what
 * `AvatarUploader` handles). Skip is instant.
 */
import React from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import AvatarUploader from "@/components/AvatarUploader";
import Brand from "@/components/Brand";
import { ArrowRight, SkipForward, Sparkle } from "@phosphor-icons/react";

function initialsOf(name) {
    if (!name) return "?";
    return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase();
}

function useNextParam() {
    const { search } = useLocation();
    const params = new URLSearchParams(search);
    // Only allow same-origin candidate routes so we don't create an
    // open redirect.
    const raw = params.get("next") || "/candidate";
    if (!raw.startsWith("/")) return "/candidate";
    return raw;
}

export default function CandidateWelcomePhoto() {
    const { user, setUser } = useAuth();
    const nav = useNavigate();
    const next = useNextParam();
    const initials = initialsOf(user?.name);
    const [hasPhoto, setHasPhoto] = React.useState(Boolean(user?.avatar_url));

    const goNext = () => nav(next, { replace: true });

    return (
        <div
            className="min-h-screen w-full flex bg-paper items-center justify-center px-6 py-12"
            data-testid="candidate-welcome-photo-page"
        >
            <div className="w-full max-w-lg">
                <div className="mb-8 flex justify-center">
                    <Brand size="h-9" />
                </div>

                <div className="bg-white border border-[#E5E5E5] rounded-2xl shadow-sm p-6 sm:p-10">
                    <div className="flex items-center justify-center gap-2 mb-2">
                        <Sparkle size={14} weight="fill" className="text-cobalt" />
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cobalt">
                            One quick thing
                        </p>
                    </div>
                    <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight text-ink text-center mb-2">
                        Add a friendly photo 👋
                    </h1>
                    <p className="text-sm text-ink/60 text-center max-w-md mx-auto mb-8">
                        Recruiters shortlist candidates with photos <span className="font-bold text-ink">3× more often</span>.
                        Takes 10 seconds — or you can add one later from your profile.
                    </p>

                    <div className="mb-8">
                        <AvatarUploader
                            initialUrl={user?.avatar_url}
                            initials={initials}
                            size="lg"
                            variant="cobalt"
                            testIdPrefix="welcome-avatar"
                            onChange={(url) => {
                                setUser({ ...user, avatar_url: url });
                                setHasPhoto(Boolean(url));
                            }}
                        />
                    </div>

                    <div className="flex flex-col-reverse sm:flex-row items-center gap-3 sm:justify-center">
                        <button
                            type="button"
                            onClick={goNext}
                            data-testid="welcome-photo-skip"
                            className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-ink/60 hover:text-ink px-4 py-2"
                        >
                            <SkipForward size={12} weight="bold" />
                            Skip for now
                        </button>
                        <button
                            type="button"
                            onClick={goNext}
                            data-testid="welcome-photo-continue"
                            className="btn-gradient inline-flex items-center gap-1.5 px-6 py-2.5 text-[11px] font-black uppercase tracking-wider rounded-full"
                        >
                            {hasPhoto ? "Looks great — continue" : "Continue"}
                            <ArrowRight size={12} weight="bold" />
                        </button>
                    </div>
                </div>

                <p className="text-[11px] text-center text-ink/40 mt-6">
                    Your photo appears only inside your full profile — never on the public browse list.
                    Change or remove it anytime from{" "}
                    <Link to="/candidate/profile" className="text-cobalt hover:underline">your profile</Link>.
                </p>
            </div>
        </div>
    );
}
